import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import CreatableSelectField from "components/CreatableSelectField";
import Field from "components/Field";
import { pick } from "lodash";
import { useMemo } from "react";
import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  IOrganizationExternalReferenceModel,
  OrganizationExternalReferenceSaveRequest,
} from "services/KnowfullyAdminClient";

const externalReferenceMaxLength = 255;

const styles = {
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
};

const { Form } = withTypes<OrganizationExternalReferenceSaveRequest>();

type OrganizationExternalReferenceFormProps = {
  organizationExternalReference?: IOrganizationExternalReferenceModel | null;
  partnerNames: string[];
  title: string;
  onCancel: () => void;
  onSubmit: (
    saveRequest: OrganizationExternalReferenceSaveRequest
  ) => Promise<object | undefined | void> | void;
};

const OrganizationExternalReferenceForm = ({
  organizationExternalReference,
  partnerNames,
  title,
  onCancel,
  onSubmit,
}: OrganizationExternalReferenceFormProps) => {
  const { formatMessage: t } = useIntl();

  const init = useMemo(
    () =>
      pick(organizationExternalReference ?? {}, [
        "id",
        "partnerName",
        "externalOrganizationId",
      ]),
    [organizationExternalReference]
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      render={({ handleSubmit, submitting, submitError }) => (
        <form onSubmit={handleSubmit}>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CreatableSelectField
                  name="partnerName"
                  label={t({ id: "partnerNameLabel" })}
                  options={partnerNames}
                  maxLength={externalReferenceMaxLength}
                  required
                />
              </Grid>

              <Field name="externalOrganizationId" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      required
                      label={t({ id: "externalOrganizationIdLabel" })}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      inputProps={{ maxLength: externalReferenceMaxLength }}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            {submitting && <CircularProgress size={24} />}
            <Button disabled={submitting} onClick={onCancel}>
              {t({ id: "cancelButton" })}
            </Button>

            <Button type="submit" color="primary" disabled={submitting}>
              {t({ id: "saveButton" })}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default OrganizationExternalReferenceForm;
